/**
 * Gym-named groups on the public sign-up page (#6005, #6010).
 *
 * The browse route sends each program's `catalog_group_id` / `catalog_sort_order`
 * and the gym's `catalog_groups`. The catalog page and the Arrange Catalog
 * window use these helpers for the card order, the group headings, and the
 * "All Groups" filter, so the order rule lives in one place.
 *
 * Every program on the page can be in a group: classes, Open Gym, private
 * lessons and tryouts. Each tab shows the groups that hold one of its
 * programs; programs in no group follow under the tab's "More …" heading.
 */

import { isAddedDateSlot } from '@/utils/classes/oneOffScheduleSlots';
import { currentWeeklyRows } from '@/utils/classes/weeklyDaySummary';
import { WEEKDAY_NAMES_SHORT } from '@/utils/constants/weekdays';
import { formatTimeTo12Hour } from '@/utils/formatters/dateUtils';

export type CatalogClassGroup = {
  id: string;
  name: string;
  sort_order: number;
};

export type CatalogScheduleSlot = {
  day_of_week?: number | null;
  start_time?: string | null;
  is_active?: boolean | null;
  effective_date?: string | null;
  end_date?: string | null;
};

export type CatalogArrangeable = {
  id: string;
  name: string;
  program_type?: string | null;
  is_open_gym?: boolean | null;
  is_tryout_event?: boolean | null;
  catalog_group_id?: string | null;
  catalog_sort_order?: number | null;
  class_schedule?: CatalogScheduleSlot[] | null;
  /** The class's session window, so a one-day clinic's own slots are not mistaken for added dates. */
  start_date?: string | null;
  end_date?: string | null;
  /** A tryout's date: `event_date` from the staff route, `specific_dates[0]` on the public page. */
  event_date?: string | null;
  specific_dates?: string[] | null;
};

export type CatalogSection<P> = {
  key: string;
  title: string | null;
  programs: P[];
};

export const MORE_CLASSES_TITLE = 'More Classes';
export const MORE_PROGRAMS_TITLE = 'More Programs';

/** The heading for programs in no group, per catalog tab. */
export const MORE_TITLE_BY_TAB: Record<string, string> = {
  all: MORE_PROGRAMS_TITLE,
  classes: MORE_CLASSES_TITLE,
  open_gym: 'More Open Gym',
  private_lessons: 'More Private Lessons',
  tryouts: 'More Tryouts',
};

/** Group names a gym may not use: the page already shows them as headings. */
export const RESERVED_GROUP_NAMES = Array.from(new Set(Object.values(MORE_TITLE_BY_TAB)));

export type CatalogItemKind = 'class' | 'private_lesson' | 'tryout';

/** What kind of row a catalog program is stored as (Open Gym is a class row), or null. */
export function catalogKindOf(program: CatalogArrangeable): CatalogItemKind | null {
  if (program.is_tryout_event === true) return 'tryout';
  if (program.program_type === 'private_lessons') return 'private_lesson';
  if (program.program_type === 'class') return 'class';
  return null;
}

/** One key per program across kinds (a tryout and a class could share an id). */
export function catalogItemKey(program: CatalogArrangeable): string {
  return `${catalogKindOf(program)}:${program.id}`;
}

export function isGroupable(program: CatalogArrangeable): boolean {
  return catalogKindOf(program) !== null;
}


function minutesOf(time: string | null | undefined): number | null {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function meetingKey(slot: CatalogScheduleSlot | null | undefined): number | null {
  if (!slot || slot.is_active === false) return null;
  const day = slot.day_of_week;
  const minutes = minutesOf(slot.start_time);
  if (typeof day !== 'number' || day < 0 || day > 6 || minutes === null) return null;
  return ((day + 6) % 7) * 24 * 60 + minutes;
}

/**
 * The earliest weekly slot. One-off added class dates never count. With the
 * gym's today, only the rows the class card shows count (`currentWeeklyRows`:
 * not ended, and in effect or the earliest upcoming), so a class that moved
 * from Monday to Wednesday sorts as Wednesday, matching its card.
 */
function firstWeeklySlot(program: CatalogArrangeable, todayStr?: string): CatalogScheduleSlot | null {
  let best: CatalogScheduleSlot | null = null;
  let bestKey = Number.POSITIVE_INFINITY;
  const window = { session_start_date: program.start_date, session_end_date: program.end_date };
  const live = (program.class_schedule || []).filter((slot) => slot && slot.is_active !== false);
  const rows = todayStr ? currentWeeklyRows(live, window, todayStr) : live.filter((slot) => !isAddedDateSlot(slot, window));
  for (const slot of rows) {
    const key = meetingKey(slot);
    if (key !== null && key < bestKey) {
      best = slot;
      bestKey = key;
    }
  }
  return best;
}

/**
 * The class's earliest weekly meeting as one number: Monday first, Sunday last,
 * then start time. A class with no weekly meeting sorts after every class that
 * has one.
 */
export function firstWeeklyMeetingKey(program: CatalogArrangeable, todayStr?: string): number {
  return meetingKey(firstWeeklySlot(program, todayStr)) ?? Number.POSITIVE_INFINITY;
}

/** "Mon 5:00 PM" for the class's earliest weekly meeting, or null. */
export function firstWeeklyMeetingLabel(program: CatalogArrangeable, todayStr?: string): string | null {
  const slot = firstWeeklySlot(program, todayStr);
  if (!slot || typeof slot.day_of_week !== 'number') return null;
  return `${WEEKDAY_NAMES_SHORT[slot.day_of_week]} ${formatTimeTo12Hour(slot.start_time)}`;
}

export function compareByDayThenTime(a: CatalogArrangeable, b: CatalogArrangeable, todayStr?: string): number {
  const keyA = firstWeeklyMeetingKey(a, todayStr);
  const keyB = firstWeeklyMeetingKey(b, todayStr);
  if (keyA !== keyB) return keyA < keyB ? -1 : 1;
  const nameCmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  if (nameCmp !== 0) return nameCmp;
  return a.id.localeCompare(b.id);
}

/** Where a program the gym never placed goes: classes, then Open Gym, then tryouts, then private lessons. */
function unplacedRank(program: CatalogArrangeable): number {
  const kind = catalogKindOf(program);
  if (kind === 'class') return program.is_open_gym ? 1 : 0;
  if (kind === 'tryout') return 2;
  if (kind === 'private_lesson') return 3;
  return 4;
}

const tryoutDate = (p: CatalogArrangeable) => p.event_date ?? p.specific_dates?.[0] ?? '';

/**
 * The one order rule, used by the sign-up page and the Arrange Catalog window
 * alike. The gym's saved order first. A program it never placed goes after:
 * classes, then Open Gym (each by day and time), then tryouts (by date), then
 * private lessons (by name).
 */
export function compareCatalogPlacement(a: CatalogArrangeable, b: CatalogArrangeable, todayStr?: string): number {
  const orderA = a.catalog_sort_order;
  const orderB = b.catalog_sort_order;
  const hasA = typeof orderA === 'number';
  const hasB = typeof orderB === 'number';
  if (hasA && hasB && orderA !== orderB) return orderA < orderB ? -1 : 1;
  if (hasA !== hasB) return hasA ? -1 : 1;
  const rankA = unplacedRank(a);
  const rankB = unplacedRank(b);
  if (rankA !== rankB) return rankA - rankB;
  if (rankA === 2) {
    const dateCmp = tryoutDate(a).localeCompare(tryoutDate(b));
    if (dateCmp !== 0) return dateCmp;
  }
  return compareByDayThenTime(a, b, todayStr);
}

export function sortCatalogGroups(groups: CatalogClassGroup[] | null | undefined): CatalogClassGroup[] {
  return [...(groups || [])].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

/**
 * The class's group, or null. A group id that is not one of this gym's groups
 * reads as no group, so a stray id can never invent a heading.
 */
export function groupIdOf(program: CatalogArrangeable, groupIds: Set<string>): string | null {
  if (!isGroupable(program)) return null;
  const id = program.catalog_group_id;
  return id && groupIds.has(id) ? id : null;
}

/**
 * The gym's groups that hold at least one of these classes, in the gym's order.
 * Headings and the "All Groups" filter come from this, so an empty group never
 * shows.
 */
export function groupsInUse<P extends CatalogArrangeable>(
  programs: P[],
  groups: CatalogClassGroup[] | null | undefined,
): CatalogClassGroup[] {
  const ordered = sortCatalogGroups(groups);
  const ids = new Set(ordered.map((g) => g.id));
  const used = new Set<string>();
  for (const program of programs) {
    const id = groupIdOf(program, ids);
    if (id) used.add(id);
  }
  return ordered.filter((g) => used.has(g.id));
}

function splitByGroup<P extends CatalogArrangeable>(
  programs: P[],
  groups: CatalogClassGroup[] | null | undefined,
): { byGroup: Map<string, P[]>; ungrouped: P[] } {
  const ids = new Set((groups || []).map((g) => g.id));
  const byGroup = new Map<string, P[]>();
  const ungrouped: P[] = [];
  for (const program of programs) {
    const id = groupIdOf(program, ids);
    if (!id) {
      ungrouped.push(program);
      continue;
    }
    byGroup.set(id, [...(byGroup.get(id) || []), program]);
  }
  return { byGroup, ungrouped };
}

/**
 * Splits one tab's cards into sections.
 *
 * - No group in use on this tab: one section with no heading, in the one order
 *   rule (`compareCatalogPlacement`): saved order first, then anything never
 *   placed — classes, then Open Gym, by day and time; tryouts by date; private
 *   lessons by name. The Arrange Catalog window lists "Not in a group" in this
 *   same order, so what staff see is what families see.
 * - Groups in use: one section per group (gym order), then `moreTitle` for the
 *   tab's programs in no group. Empty sections are dropped.
 *
 * `inUse` comes from the tab's full list, not the filtered one, so filtering
 * never flips the page between the two layouts.
 */
export function arrangeCatalogSections<P extends CatalogArrangeable>(
  programs: P[],
  options: {
    groups: CatalogClassGroup[] | null | undefined;
    inUse: CatalogClassGroup[];
    moreTitle: string;
    /** The gym's today (YYYY-MM-DD), so ended and not-yet-started weekly rows do not decide the order. */
    todayStr?: string;
  },
): CatalogSection<P>[] {
  const byPlacement = (a: P, b: P) => compareCatalogPlacement(a, b, options.todayStr);

  if (options.inUse.length === 0) {
    const ordered = [...programs].sort(byPlacement);
    return ordered.length ? [{ key: 'all', title: null, programs: ordered }] : [];
  }

  const { byGroup, ungrouped } = splitByGroup(programs, options.groups);
  const sections: CatalogSection<P>[] = [];
  for (const group of options.inUse) {
    const list = byGroup.get(group.id);
    if (list && list.length) {
      sections.push({ key: `group:${group.id}`, title: group.name, programs: list.sort(byPlacement) });
    }
  }
  if (ungrouped.length) {
    sections.push({ key: 'more', title: options.moreTitle, programs: ungrouped.sort(byPlacement) });
  }
  return sections;
}

/**
 * The Arrange Catalog window's starting point: each of the gym's groups with
 * its programs in the order parents see them, then the programs in no group.
 * Programs are named by `catalogItemKey`.
 */
export function buildArrangementDraft<P extends CatalogArrangeable>(
  programs: P[],
  groups: CatalogClassGroup[] | null | undefined,
  todayStr?: string,
): { groups: Array<{ id: string; name: string; itemKeys: string[] }>; ungroupedKeys: string[] } {
  const byPlacement = (a: P, b: P) => compareCatalogPlacement(a, b, todayStr);
  const ordered = sortCatalogGroups(groups);
  const { byGroup, ungrouped } = splitByGroup(programs.filter(isGroupable), ordered);
  return {
    groups: ordered.map((g) => ({
      id: g.id,
      name: g.name,
      itemKeys: (byGroup.get(g.id) || []).sort(byPlacement).map(catalogItemKey),
    })),
    ungroupedKeys: ungrouped.sort(byPlacement).map(catalogItemKey),
  };
}
